// src/utils/uploadUtils.js
import {
  storageRef as ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
} from "@/services/legacyBrowserPersistenceBoundary";
import { auth } from "@/firebase/authorityClient";

const storage = null;
const IMMUTABLE_MEDIA_CACHE_CONTROL =
  "public, max-age=31536000, immutable";
const UID_OWNED_MEDIA_ROOTS = new Set([
  "profile_pictures",
  "products",
  "services",
  "images",
  "videos",
  "audios",
  "documents",
  "multimedia",
]);

const safePathSegment = (value, label) => {
  const segment = String(value || "").trim();
  if (
    !segment ||
    segment === "." ||
    segment === ".." ||
    segment.includes("/") ||
    segment.includes("\\") ||
    /[\u0000-\u001f\u007f]/u.test(segment)
  ) {
    throw new Error(`${label} is invalid`);
  }
  return segment;
};

const authenticatedUploadOwner = (requestedOwnerUid) => {
  const authenticatedUid = auth.currentUser?.uid || null;
  if (!authenticatedUid) throw new Error("Authentication is required for uploads");
  safePathSegment(authenticatedUid, "Authenticated UID");
  if (requestedOwnerUid && requestedOwnerUid !== authenticatedUid) {
    throw new Error("The upload owner does not match the authenticated account");
  }
  return authenticatedUid;
};

export const buildOwnedUploadPath = ({ folder, filename, ownerUid }) => {
  const segments = String(folder || "images")
    .split("/")
    .filter(Boolean)
    .map((segment) => safePathSegment(segment, "Upload folder"));
  const root = segments.shift() || "images";
  const safeFilename = safePathSegment(filename, "Upload filename");
  if (!UID_OWNED_MEDIA_ROOTS.has(root)) {
    return [root, ...segments, safeFilename].join("/");
  }
  const uid = authenticatedUploadOwner(ownerUid);
  if (segments[0] === uid) segments.shift();
  return [root, uid, ...segments, safeFilename].join("/");
};

const buildMediaMetadata = (file) => ({
  contentType: file?.type || "application/octet-stream",
  cacheControl: IMMUTABLE_MEDIA_CACHE_CONTROL,
  customMetadata: {
    uploadedAt: new Date().toISOString(),
  },
});

// Existing function for P5.js files (unchanged)
export const uploadP5File = async (file, { ownerUid } = {}) => {
  if (!file) throw new Error("No file provided for upload");
  const path = buildOwnedUploadPath({
    folder: "multimedia",
    filename: `${Date.now()}_${file.name}`,
    ownerUid,
  });
  const fileRef = ref(storage, path);
  try {
    await uploadBytes(fileRef, file);
    const fileUrl = await getDownloadURL(fileRef);
    return fileUrl;
  } catch (error) {
    console.error("Error uploading P5.js file:", error);
    throw error;
  }
};

// New function for compressing GIFs
export const compressGif = async (gifFile) => {
  try {
    // Note: You'll need to install this package with npm or yarn
    // npm install gif-compressor --save
    // For now, just log a message about future implementation
    console.log(
      "GIF compression would be implemented here with gif-compressor library"
    );
    console.log(
      "To implement, install the library with: npm install gif-compressor --save"
    );

    // For now, return the original file
    return gifFile;

    /* Implementation would look like this:
    import GifCompressor from 'gif-compressor';
    
    const options = {
      quality: 10, // 10 is the lowest quality, 1 is the highest
      workers: navigator.hardwareConcurrency || 4
    };
    
    return await GifCompressor.compress(gifFile, options);
    */
  } catch (error) {
    console.error("Error compressing GIF:", error);
    // Return original file if compression fails
    return gifFile;
  }
};

// New function for compressing videos
export const compressVideo = async (videoFile) => {
  try {
    // Note: You'll need to install this package
    // npm install @ffmpeg/ffmpeg @ffmpeg/core --save
    console.log(
      "Video compression would be implemented here with ffmpeg.wasm library"
    );
    console.log(
      "To implement, install the library with: npm install @ffmpeg/ffmpeg @ffmpeg/core --save"
    );

    // For now, return the original file
    return videoFile;

    /* Implementation would look like this:
    import { createFFmpeg, fetchFile } from '@ffmpeg/ffmpeg';
    
    const ffmpeg = createFFmpeg({ log: true });
    await ffmpeg.load();
    
    const name = videoFile.name;
    const data = await fetchFile(videoFile);
    ffmpeg.FS('writeFile', name, data);
    
    // Run the FFmpeg command for compression
    await ffmpeg.run(
      '-i', name,
      '-vcodec', 'libx264',
      '-crf', '28', // Compression level (23 is default, lower = better quality, higher = smaller size)
      '-preset', 'medium', // Encoding speed/compression trade-off
      'output.mp4'
    );
    
    // Read the result
    const compressedData = ffmpeg.FS('readFile', 'output.mp4');
    
    // Create a new file from the compressed data
    const compressedFile = new File(
      [compressedData.buffer],
      videoFile.name.replace(/\.[^/.]+$/, '.mp4'),
      { type: 'video/mp4' }
    );
    
    return compressedFile;
    */
  } catch (error) {
    console.error("Error compressing video:", error);
    // Return original file if compression fails
    return videoFile;
  }
};

// Enhanced folder determination with comprehensive document support
function determineFolder(mimeType) {
  if (!mimeType) return "images";

  if (mimeType.startsWith("image/")) return "images";
  if (mimeType.startsWith("video/")) return "videos";
  if (mimeType.startsWith("audio/")) return "audios";

  // Enhanced document detection
  if (
    mimeType === "application/pdf" ||
    mimeType.startsWith("application/msword") ||
    mimeType.startsWith("application/vnd.openxmlformats") ||
    mimeType.startsWith("text/") ||
    mimeType.includes("document") ||
    mimeType.includes("spreadsheet")
  ) {
    return "documents";
  }

  console.warn("Unknown MIME type, defaulting to images:", mimeType);
  return "images";
}

// New function for general media files
export const uploadMediaFile = async (file, options = {}) => {
  if (!file) throw new Error("No file provided for upload");

  const {
    folder: overrideFolder,
    ownerUid: requestedOwnerUid,
    returnDetailed = false,
  } = options;

  // Check if the file is a URL string (existing logic unchanged)
  if (typeof file === "string") {
    if (file.startsWith("blob:")) {
      console.log("File is a Blob URL - skipping upload:", file);
    } else if (file.startsWith("http")) {
      console.log("File is already a remote URL - skipping upload:", file);
    } else {
      console.log("File is a string but not a recognized URL format:", file);
    }
    return returnDetailed ? { url: file, path: null, contentType: null } : file;
  }

  if (!(file instanceof Blob) && !(file instanceof File)) {
    throw new Error("Invalid file object");
  }

  // Process file based on type (existing compression logic unchanged)
  let processedFile = file;
  if (file.type) {
    if (file.type === "image/gif") {
      console.log("Processing GIF file:", file.name);
      processedFile = await compressGif(file);
    } else if (file.type.startsWith("video/")) {
      console.log("Processing video file:", file.name);
      processedFile = await compressVideo(file);
    }
  }

  // Enhanced folder determination with document support
  let folder = overrideFolder || "images"; // Use override or default

  if (!overrideFolder && file.type) {
    folder = determineFolder(file.type);
  } else if (!overrideFolder && !file.type) {
    console.warn("File has no type property, defaulting to images folder");
  }

  const filename = `${Date.now()}_${processedFile.name || "untitled"}`;
  const path = buildOwnedUploadPath({
    folder,
    filename,
    ownerUid: requestedOwnerUid,
  });
  const fileRef = ref(storage, path);

  try {
    console.log(`Uploading file to ${folder}...`);

    // Set contentType metadata for proper serving
    const metadata = buildMediaMetadata(processedFile);
    await uploadBytes(fileRef, processedFile, metadata);

    const fileUrl = await getDownloadURL(fileRef);
    console.log(`[uploadMediaFile] Uploaded file to ${path}: ${fileUrl}`);

    // Return detailed info if requested, otherwise maintain backward compatibility
    if (returnDetailed) {
      return {
        url: fileUrl,
        path: path,
        contentType: processedFile.type || null,
        folder: folder,
      };
    }

    return fileUrl;
  } catch (error) {
    console.error("Error uploading media file:", error);
    throw error;
  }
};

/**
 * Upload a media file with progress tracking using Firebase's uploadBytesResumable.
 * @param {File|Blob} file - The file to upload
 * @param {Object} options - Upload options
 * @param {string} options.folder - Override folder destination
 * @param {boolean} options.returnDetailed - Return detailed info object
 * @param {Function} options.onProgress - Callback receiving progress (0-100)
 * @param {AbortSignal} options.signal - Optional abort signal for cancellation
 * @returns {Promise<string|Object>} URL string or detailed info object
 */
export const uploadMediaFileWithProgress = async (file, options = {}) => {
  if (!file) throw new Error("No file provided for upload");

  const { 
    folder: overrideFolder, 
    returnDetailed = false, 
    onProgress,
    signal,
    ownerUid: requestedOwnerUid,
  } = options;

  // Handle URL strings (same as uploadMediaFile)
  if (typeof file === "string") {
    if (file.startsWith("blob:") || file.startsWith("http")) {
      console.log("File is already a URL - skipping upload:", file);
    }
    return returnDetailed ? { url: file, path: null, contentType: null } : file;
  }

  if (!(file instanceof Blob) && !(file instanceof File)) {
    throw new Error("Invalid file object");
  }

  // Process file based on type (compression)
  let processedFile = file;
  if (file.type) {
    if (file.type === "image/gif") {
      console.log("Processing GIF file:", file.name);
      processedFile = await compressGif(file);
    } else if (file.type.startsWith("video/")) {
      console.log("Processing video file:", file.name);
      processedFile = await compressVideo(file);
    }
  }

  // Determine folder
  let folder = overrideFolder || "images";
  if (!overrideFolder && file.type) {
    folder = determineFolder(file.type);
  }
  const filename = `${Date.now()}_${processedFile.name || "untitled"}`;
  const path = buildOwnedUploadPath({
    folder,
    filename,
    ownerUid: requestedOwnerUid,
  });
  const fileRef = ref(storage, path);

  const metadata = buildMediaMetadata(processedFile);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(fileRef, processedFile, metadata);

    // Handle optional abort signal
    if (signal) {
      signal.addEventListener("abort", () => {
        uploadTask.cancel();
        reject(new Error("Upload cancelled"));
      }, { once: true });
    }

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Progress tracking
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (typeof onProgress === "function") {
          onProgress(Math.round(progress));
        }
        console.log(`[uploadMediaFileWithProgress] ${path}: ${progress.toFixed(1)}%`);
      },
      (error) => {
        // Error handling
        console.error("Error uploading media file with progress:", error);
        reject(error);
      },
      async () => {
        // Upload completed successfully
        try {
          const fileUrl = await getDownloadURL(uploadTask.snapshot.ref);
          console.log(`[uploadMediaFileWithProgress] Completed: ${fileUrl}`);

          if (returnDetailed) {
            resolve({
              url: fileUrl,
              path: path,
              contentType: processedFile.type || null,
              folder: folder,
            });
          } else {
            resolve(fileUrl);
          }
        } catch (urlError) {
          reject(urlError);
        }
      }
    );
  });
};

const waitForVideoEvent = (video, eventNames, timeoutMs, errorMessage) =>
  new Promise((resolve, reject) => {
    let done = false;
    const cleanup = () => {
      eventNames.forEach((name) => video.removeEventListener(name, onReady));
      video.removeEventListener("error", onError);
      clearTimeout(timer);
    };
    const finish = (fn, value) => {
      if (done) return;
      done = true;
      cleanup();
      fn(value);
    };
    const onReady = () => finish(resolve);
    const onError = () =>
      finish(
        reject,
        new Error(video.error?.message || errorMessage || "Video load failed"),
      );
    const timer = setTimeout(
      () => finish(reject, new Error(errorMessage || "Video timed out")),
      timeoutMs,
    );
    eventNames.forEach((name) =>
      video.addEventListener(name, onReady, { once: true }),
    );
    video.addEventListener("error", onError, { once: true });
  });

export const captureVideoPosterFrame = async (
  videoFileOrUrl,
  {
    atTimeSec = 0.1,
    preferPng = false,
    quality = 0.72,
    maxWidth = 1024,
    timeoutMs = 8000,
    createObjectUrl = false,
  } = {},
) => {
  const isUrl = typeof videoFileOrUrl === "string";
  const isBlob =
    typeof Blob !== "undefined" && videoFileOrUrl instanceof Blob;
  if (!isUrl && !isBlob) {
    throw new Error("Invalid video input for poster generation");
  }

  let src = videoFileOrUrl;
  let revokeSourceUrl = null;
  if (!isUrl) {
    src = URL.createObjectURL(videoFileOrUrl);
    revokeSourceUrl = src;
  }

  try {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    if (isUrl && !String(src).startsWith("blob:")) {
      video.crossOrigin = "anonymous";
    }
    video.src = src;

    if (typeof video.load === "function") video.load();

    if (video.readyState < 1) {
      await waitForVideoEvent(
        video,
        ["loadedmetadata", "loadeddata"],
        timeoutMs,
        "Video metadata timed out",
      );
    }

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const targetTime =
      Number.isFinite(atTimeSec) && atTimeSec > 0
        ? Math.min(atTimeSec, duration > 0 ? Math.max(0, duration - 0.05) : atTimeSec)
        : 0;

    if (targetTime > 0 && Math.abs((video.currentTime || 0) - targetTime) > 0.03) {
      const seekPromise = waitForVideoEvent(
        video,
        ["seeked"],
        timeoutMs,
        "Video seek timed out",
      ).catch(() => {});
      video.currentTime = targetTime;
      await seekPromise;
    }

    if (video.readyState < 2) {
      await waitForVideoEvent(
        video,
        ["loadeddata", "canplay"],
        timeoutMs,
        "Video frame timed out",
      ).catch(() => {});
    }

    const sourceWidth = video.videoWidth || 640;
    const sourceHeight = video.videoHeight || 360;
    const scale = Math.min(1, maxWidth / sourceWidth);
    const w = Math.max(1, Math.round(sourceWidth * scale));
    const h = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas context unavailable");
    ctx.drawImage(video, 0, 0, w, h);

    const mime = preferPng ? "image/png" : "image/jpeg";
    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
        mime,
        quality,
      ),
    );
    const file = new File([blob], `poster.${preferPng ? "png" : "jpg"}`, {
      type: mime,
    });

    return {
      blob,
      file,
      width: w,
      height: h,
      objectUrl: createObjectUrl ? URL.createObjectURL(blob) : "",
    };
  } finally {
    if (revokeSourceUrl) URL.revokeObjectURL(revokeSourceUrl);
  }
};

// Generate a poster image for a video by capturing a frame and uploading it.
// Returns { url } of the uploaded poster image. Falls back gracefully.
export const generateVideoPoster = async (
  videoFileOrUrl,
  {
    folder = "images",
    atTimeSec = 0.1,
    preferPng = false,
    ownerUid,
  } = {},
) => {
  try {
    const frame = await captureVideoPosterFrame(videoFileOrUrl, {
      atTimeSec,
      preferPng,
    });
    const filename = `${Date.now()}_video_poster.${preferPng ? "png" : "jpg"}`;
    const path = buildOwnedUploadPath({ folder, filename, ownerUid });
    const fileRef = ref(storage, path);
    const snapshot = await uploadBytes(
      fileRef,
      frame.file,
      buildMediaMetadata(frame.file),
    );
    const posterUrl = await getDownloadURL(snapshot.ref);
    return { url: posterUrl };
  } catch (e) {
    console.warn("[uploadUtils] Failed to generate video poster:", e);
    return { url: null };
  }
};

// -------------------------------
// Responsive Image Variants Utils
// -------------------------------

// Helper: sanitize a string for use in file paths
const sanitizeForPath = (str) =>
  (str || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // remove diacritics
    .replace(/[^a-zA-Z0-9-_\.]+/g, "-") // keep safe chars
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// Read an image as HTMLImageElement
const loadImage = (file) =>
  new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });

// Resize using canvas to a target width, preserving aspect ratio
const resizeImageToWidth = async (img, targetWidth, options = {}) => {
  const { mime = "image/webp", quality = 0.84 } = options;
  const ratio = targetWidth / img.width;
  const w = Math.min(targetWidth, img.width);
  const h = Math.round(img.height * (w / img.width));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas toBlob failed"));
          const ext = mime.includes("png")
            ? "png"
            : mime.includes("webp")
            ? "webp"
            : "jpg";
          const file = new File([blob], `resized.${ext}`, { type: mime });
          resolve(file);
        },
        mime,
        quality
      );
    } catch (e) {
      reject(e);
    }
  });
};

// Upload a single file to a specific storage path and return URL
const uploadToStorage = async (folder, filename, file, ownerUid) => {
  const path = buildOwnedUploadPath({ folder, filename, ownerUid });
  const fileRef = ref(storage, path);
  const snapshot = await uploadBytes(
    fileRef,
    file,
    buildMediaMetadata(file),
  );
  return await getDownloadURL(snapshot.ref);
};

// Public API: generate and upload responsive variants for an image file
// Returns: { url, variants } where url is the largest (xl) or the best available
export const uploadImageWithVariants = async (
  file,
  {
    folder = "images",
    widths = [256, 512, 1024, 1920],
    baseName = "",
    preferPng = false,
    quality = 0.84,
    thumbnailQuality = 0.72,
    ownerUid,
  } = {}
) => {
  if (!file) throw new Error("No file provided for upload");
  if (!(file instanceof File) && !(file instanceof Blob)) {
    // Allow string URLs to flow through for compatibility
    if (typeof file === "string") return { url: file, variants: {} };
    throw new Error("Invalid image file for variants");
  }

  // Bypass for GIFs (preserve animation) and non-images
  if (file.type === "image/gif" || !file.type?.startsWith("image/")) {
    const filename = `${Date.now()}_${sanitizeForPath(file.name || "image")}`;
    const url = await uploadToStorage(folder, filename, file, ownerUid);
    return { url, variants: {} };
  }

  const originalImg = await loadImage(file);

  // Decide output type
  // WebP provides a substantial transfer reduction while preserving alpha.
  // Keep PNG only when a caller explicitly requires that format.
  const isPng = preferPng;
  const mime = isPng ? "image/png" : "image/webp";

  // Ensure stable base name
  const origName = (file.name || "image").replace(/\.[^.]+$/, "");
  const safeBase = sanitizeForPath(baseName || `${Date.now()}_${origName}`);

  // Only generate widths <= original
  const fullDisplayWidth = Math.min(1920, originalImg.width);
  const effectiveWidths = [
    ...new Set([
      ...widths.filter((w) => w > 0 && w <= originalImg.width),
      fullDisplayWidth,
    ]),
  ].sort((a, b) => a - b);

  const variants = {};
  const semanticKeyByWidth = {
    256: "sm",
    512: "md",
    1024: "lg",
    1920: "xl",
    2560: "xxl",
  };
  const largestWidth = Math.max(...effectiveWidths);

  const uploadedVariants = await Promise.all(
    effectiveWidths.map(async (w) => {
      const resized = await resizeImageToWidth(originalImg, w, {
        mime,
        quality:
          isPng || w === largestWidth ? quality : thumbnailQuality,
      });
      const ext = isPng ? "png" : "webp";
      const filename = `${safeBase}_w${w}.${ext}`;
      const url = await uploadToStorage(folder, filename, resized, ownerUid);
      return { key: semanticKeyByWidth[w] || `w${w}`, url };
    }),
  );

  uploadedVariants.forEach(({ key, url }) => {
    variants[key] = url;
  });

  // Choose best fallback url (largest width we generated)
  let largestUrl =
    variants.xxl ||
    variants.xl ||
    variants.lg ||
    variants.md ||
    variants.sm ||
    null;

  if (!largestUrl) {
    const vals = Object.values(variants);
    largestUrl = vals.length ? vals[vals.length - 1] : null;
  }

  return { url: largestUrl, variants };
};

// Public API: process many images
export const uploadManyImagesWithVariants = async (files, options) => {
  const results = [];
  for (const f of files || []) {
    if (!(f instanceof File)) continue;
    try {
      const res = await uploadImageWithVariants(f, options);
      results.push(res);
    } catch (e) {
      console.warn(
        "uploadManyImagesWithVariants: variant generation failed; falling back",
        e
      );
      const filename = `${Date.now()}_${sanitizeForPath(f.name || "image")}`;
      const url = await uploadToStorage(
        options?.folder || "images",
        filename,
        f,
        options?.ownerUid,
      );
      results.push({ url, variants: {} });
    }
  }
  return results;
};
